// src/components/Login/Login.jsx
"use client"

import { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { GoogleAuthProvider } from "firebase/auth";
import "./Login.css";
import { useLocation, useNavigate } from "react-router-dom";
import googleLogo from "../../assets/glogo.webp";
import { toast } from "react-toastify";
import { useAuth } from "../../utils/AuthContext";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import BackButton from "../BackButton/BackButton";
import Spinner from "../Spinner/Spinner";
import { getUserData, setgLogin } from "../../utils/auth";
import FloatingField from "../FloatingField/FloatingField";
import LocationTermsModal from "../LocationTermsModal/LocationTermsModal";
import { FiUserPlus, FiStar, FiHeart, FiMessageCircle, FiGift, FiShield } from "react-icons/fi";

const Login = () => {
    const { handleLogin, handleGoogleSignIn, handlePasswordReset } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [loadingGoogle, setLoadingGoogle] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const provider = new GoogleAuthProvider();
    const [showLocationModal, setShowLocationModal] = useState(false);
    const locationSite = useLocation();

    // Estado para controlar si el usuario debe completar información obligatoria
    const [mustCompleteInfo, setMustCompleteInfo] = useState(false);

    // Resuelve `from` (puede ser string legacy o {pathname, search}) a un string navegable.
    const resolvePreviousPath = () => {
        const raw = locationSite.state?.from;
        if (!raw) return null;
        if (typeof raw === 'string') return raw;
        if (typeof raw === 'object' && raw.pathname) {
            return `${raw.pathname}${raw.search || ''}`;
        }
        return null;
    };

    // Enhanced beforeunload handler that also shows warning for internal navigation attempts
    useEffect(() => {
        if (mustCompleteInfo) {
            const handleBeforeUnload = (e) => {
                e.preventDefault();
                e.returnValue = "Debes completar tu ubicación y aceptar los términos antes de salir.";
                return "Debes completar tu ubicación y aceptar los términos antes de salir.";
            };

            // Add a custom navigation warning
            const handlePopState = (e) => {
                if (mustCompleteInfo) {
                    const shouldStay = window.confirm("Debes completar tu ubicación y aceptar los términos antes de continuar. ¿Estás seguro de que quieres salir?");
                    if (!shouldStay) {
                        window.history.pushState(null, null, window.location.pathname);
                    }
                }
            };

            window.addEventListener('beforeunload', handleBeforeUnload);
            window.addEventListener('popstate', handlePopState);

            // Push a dummy state to handle back button
            window.history.pushState(null, null, window.location.pathname);

            return () => {
                window.removeEventListener('beforeunload', handleBeforeUnload);
                window.removeEventListener('popstate', handlePopState);
            };
        }
    }, [mustCompleteInfo]);

    // Si ya hay datos del usuario, redirige al home
    useEffect(() => {
        const user = getUserData();
        if (user) {
            // Verificar si el usuario ya tiene términos aceptados y ubicación
            if (!user.location || user.termsAccepted === false || user.termsAccepted === null) {
                setShowLocationModal(true);
                setMustCompleteInfo(true); // Activar bloqueo de navegación
            } else {
                navigate("/");
            }
        }
    }, [navigate]);

    const onLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await handleLogin(email, password);
            const userData = getUserData();

            // Verificar si el usuario necesita completar la ubicación o aceptar términos
            if (userData && (!userData.location || userData.location === null || !userData.termsAccepted || userData.termsAccepted === false || userData.termsAccepted === null)) {
                setShowLocationModal(true);
                setMustCompleteInfo(true); // Activar bloqueo de navegación
            } else {
                const previousPath = resolvePreviousPath();
                if (previousPath && previousPath !== "/registro" && previousPath !== "/login" &&
                    !previousPath.startsWith("/registro?") && !previousPath.startsWith("/login?")) {
                    navigate(previousPath);
                } else {
                    navigate("/");
                }
            }
        } catch (error) {
            // El manejo de errores específicos ya está en el AuthContext,
            // solo muestra el mensaje que devuelve.
            if (error.code === "auth/wrong-password" || error.code === "auth/user-not-found" || error.code.toString().includes("auth/invalid-credential")) {
                toast.error("Correo o contraseña incorrectos.");
            } else {
                console.error("Error al iniciar sesión:", error);
                toast.error("Ocurrió un error al iniciar sesión. Intenta nuevamente.");
            }
        } finally {
            setLoading(false);
        }
    };

    const onGoogleLogin = async (e) => {
        e.preventDefault();
        setLoadingGoogle(true);
        try {
            const locationRequired = await handleGoogleSignIn(provider);
            if (locationRequired) {
                toast.info("Por favor, completa tu localidad y acepta los términos para continuar.");
                setShowLocationModal(true);
                setMustCompleteInfo(true); // Activar bloqueo de navegación
            } else {
                const userData = getUserData();
                // Verificamos si el usuario ya aceptó los términos anteriormente
                if (userData && !userData.termsAccepted) {
                    toast.info("Es necesario aceptar los términos y condiciones para continuar.");
                    setShowLocationModal(true);
                    setMustCompleteInfo(true); // Activar bloqueo de navegación
                } else {
                    const previousPath = resolvePreviousPath();
                    if (previousPath && previousPath !== "/registro" && previousPath !== "/login" &&
                        !previousPath.startsWith("/registro?") && !previousPath.startsWith("/login?")) {
                        navigate(previousPath);
                    } else {
                        navigate("/");
                    }
                }
            }
        } catch (error) {
            console.error("Error al iniciar sesión con Google:", error);
        } finally {
            setLoadingGoogle(false);
        }
    };

    const onPasswordReset = async () => {
        if (!email) {
            toast.error("Por favor, ingresa tu correo electrónico para restablecer la contraseña.");
            return;
        }
        setLoading(true);
        try {
            await handlePasswordReset(email);
            toast.success("Se ha enviado un correo para restablecer tu contraseña (revisa spam).");
        } catch (error) {
            console.error("Error enviando el correo de restablecimiento:", error);
            toast.error("Error al enviar el correo de restablecimiento de contraseña.");
        } finally {
            setLoading(false);
        }
    };

    const handleSignUpRedirect = () => {
        if (mustCompleteInfo) {
            toast.warning("Debes completar tu información antes de continuar.");
            return;
        }
        navigate("/registro");
    };

    const handleLocationModalSuccess = () => {
        setShowLocationModal(false);
        setMustCompleteInfo(false); // Desactivar bloqueo de navegación

        const previousPath = resolvePreviousPath();
        if (previousPath && previousPath !== "/registro" && previousPath !== "/login" &&
            !previousPath.startsWith("/registro?") && !previousPath.startsWith("/login?")) {
            navigate(previousPath);
        } else {
            navigate("/");
        }
    };

    const handleLocationModalClose = () => {
        if (mustCompleteInfo) {
            toast.warning("Debes completar tu información antes de continuar.");
        } else {
            setShowLocationModal(false);
        }
    };

    return (
        <div className="container">
            <Helmet>
                <title>Iniciar Sesión | ContactaMe</title>
                <meta
                    name="description"
                    content="Iniciá sesión en ContactaMe para acceder a tu cuenta, gestionar tu perfil de proveedor y conectar con clientes. Ingresá con email o Google."
                />
                <meta
                    name="keywords"
                    content="iniciar sesión, login, contactame, acceder cuenta, ingresar"
                />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="website" />
                <meta property="og:url" content="https://contactame.com.ar/login" />
                <meta property="og:title" content="Iniciar Sesión | ContactaMe" />
                <meta property="og:description" content="Iniciá sesión en ContactaMe para acceder a tu cuenta." />
                <meta property="og:image" content="https://contactame.com.ar/logo512.png" />

                {/* Twitter */}
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:url" content="https://contactame.com.ar/login" />
                <meta name="twitter:title" content="Iniciar Sesión | ContactaMe" />
                <meta name="twitter:description" content="Iniciá sesión en ContactaMe para acceder a tu cuenta." />
                <meta name="twitter:image" content="https://contactame.com.ar/logo512.png" />

                {/* Canonical URL */}
                <link rel="canonical" href="https://contactame.com.ar/login" />

                {/* Robots - no indexar páginas de login */}
                <meta name="robots" content="noindex, nofollow" />
            </Helmet>

            {(loading || loadingGoogle) && <Spinner />}
            {!mustCompleteInfo && <BackButton to="/" />}
            <div className="login-container">
                <div className="login-column">
                    <div className="card">
                        <h2>Iniciar sesión</h2>
                        <form onSubmit={onLogin}>
                            <FloatingField value={email} label="Correo electrónico" id="email">
                                <input
                                    id="email"
                                    name="email"
                                    type="email"
                                    className="form-control"
                                    placeholder=" "
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    disabled={loading || loadingGoogle || mustCompleteInfo}
                                />
                            </FloatingField>
                            <FloatingField
                                value={password}
                                label="Contraseña"
                                id="password"
                                isPassword={true}
                                onToggle={() => setShowPassword(!showPassword)}
                                toggleIcon={showPassword ? <FaEyeSlash /> : <FaEye />}
                            >
                                <input
                                    id="password"
                                    name="password"
                                    type={showPassword ? "text" : "password"}
                                    className="form-control"
                                    placeholder=" "
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    disabled={loading || loadingGoogle || mustCompleteInfo}
                                />
                            </FloatingField>
                            <button type="submit" className="new-btn" style={{backgroundColor:"#0056b3", color:"#f0f0f0"}} disabled={loading || loadingGoogle || mustCompleteInfo}>
                                {loading ? "Iniciando..." : "Iniciar sesión"}
                            </button>
                        </form>
                        <button className="new-btn btn-light btn-link mt-3" style={{marginTop:"5px", boxShadow:"none"}} onClick={onPasswordReset} disabled={loading || loadingGoogle || mustCompleteInfo}>
                            ¿Olvidaste tu contraseña?
                        </button>
                    </div>

                    <div className="card google-card" style={{marginTop: "0px"}}>
                        <button
                            className="new-btn btn-outline-gray google-button"
                            onClick={onGoogleLogin}
                            disabled={loading || loadingGoogle || mustCompleteInfo}
                        >
                            <img src={googleLogo || "/placeholder.svg"} alt="Google Logo" className="google-logo" />
                            {loadingGoogle ? "Iniciando..." : "Ingresar con Google"}
                        </button>
                    </div>
                </div>

                {/* Card de Información para Registrarse - rediseñada */}
                <div className="card info register-card" style={{marginBottom:"0px"}}>
                    <div className="rc-header">
                        <h2 className="rc-title">¿Aún no tenés cuenta?</h2>
                        <p className="rc-subtitle">Sumate gratis y obtené beneficios pensados para vos.</p>
                    </div>

                    <ul className="rc-features nul">
                        <li className="rc-feature">
                            <span className="rc-icon success"><FiHeart /></span>
                            <div className="rc-text">
                                <span className="rc-feature-title">Guardá tus favoritos</span>
                                <span className="rc-feature-desc">Tené a mano tus contactos preferidos.</span>
                            </div>
                        </li>
                        <li className="rc-feature">
                            <span className="rc-icon success"><FiMessageCircle /></span>
                            <div className="rc-text">
                                <span className="rc-feature-title">Opiná, preguntá y recomendá</span>
                                <span className="rc-feature-desc">Dejá preguntas o reseñas y ayudá a otros a elegir mejor.</span>
                            </div>
                        </li>
                        <li className="rc-feature highlight">
                            <span className="rc-icon highlight"><FiStar /></span>
                            <div className="rc-text">
                                <span className="rc-feature-title">Ofrecé tu servicio o producto</span>
                                <span className="rc-feature-desc">Mostrate a futuros clientes y crecé.</span>
                            </div>
                        </li>
                    </ul>

                    <div className="rc-cta">
                        <button className="new-btn register-btn rc-register-btn" style={{backgroundColor:"#cc1e00"}} onClick={handleSignUpRedirect} disabled={loading || loadingGoogle || mustCompleteInfo}>
                            <FiUserPlus className="rc-cta-icon" /> Registrarme
                        </button>
                    </div>
                </div>
            </div>

            {/* Modal de Ubicación usando el nuevo componente */}
            <LocationTermsModal
                isOpen={showLocationModal}
                onClose={handleLocationModalClose}
                onSuccess={handleLocationModalSuccess}
                isMandatory={mustCompleteInfo}
            />
        </div>
    );
};

export default Login;
